import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Header } from "@/components/landing/Header";
import { Footer } from "@/components/landing/Footer";
import "@/pages/QuestionsFallback.css";

const CLUSTERS = [
  ["recognising-perimenopause-cycles-and-bleeding", "Recognising Perimenopause, Cycles and Bleeding", "Clear, cautious answers about recognising symptom patterns, age, periods and bleeding changes."],
  ["sleep-heat-and-physical-symptoms", "Sleep, Heat and Physical Symptoms", "Questions about broken sleep, night waking, hot flushes and commonly reported physical changes."],
  ["mood-brain-and-daily-life", "Mood, Brain and Daily Life", "Careful explanations of anxiety, low mood, irritability, cognition, work and relationships."],
  ["hormones-testing-treatment-and-self-care", "Hormones, Testing, Treatment and Self-care", "What hormone tests, HRT, medicines, supplements and everyday measures can—and cannot—tell or do."],
  ["getting-care-safety-and-lived-experience", "Getting Care, Safety and Lived Experience", "Practical guidance for appointments, finding care, recognising red flags and using lived experience safely."],
];

const clusterBySlug = Object.fromEntries(CLUSTERS.map(([slug, title, intro]) => [slug, { slug, title, intro }]));

export default function QuestionsFallback() {
  const { "*": slug = "" } = useParams();
  const [questions, setQuestions] = useState([]);
  const [failed, setFailed] = useState(false);
  const cluster = clusterBySlug[slug];

  useEffect(() => {
    let active = true;
    fetch("/questions-data/questions.json")
      .then((response) => { if (!response.ok) throw new Error("Questions unavailable"); return response.json(); })
      .then((data) => { if (active) setQuestions(data); })
      .catch(() => { if (active) setFailed(true); });
    return () => { active = false; };
  }, []);

  const clusterQuestions = useMemo(() => questions.filter((question) => question.cluster === slug), [questions, slug]);
  useEffect(() => { document.title = cluster ? `${cluster.title} | Supple` : "Perimenopause Questions | Supple"; }, [cluster]);

  return (
    <div className="questions-fallback-page" data-testid="questions-fallback-page">
      <Header />
      <main className="questions-fallback-main">
        {cluster ? (
          <>
            <nav className="questions-breadcrumb" aria-label="Breadcrumb" data-testid="questions-breadcrumb">
              <Link to="/">Home</Link><span>/</span><a href="/questions">Questions</a><span>/</span><span>{cluster.title}</span>
            </nav>
            <header className="questions-fallback-hero">
              <p className="eyebrow">Questions</p>
              <h1 data-testid="questions-cluster-heading">{cluster.title}</h1>
              <p>{cluster.intro}</p>
            </header>
            <section className="questions-answer-list" aria-label={`${cluster.title} answers`} data-testid="questions-answer-list">
              {clusterQuestions.map((question, index) => (
                <article id={question.anchor} key={question.anchor} data-testid={`questions-answer-${index}`}>
                  <h2>{question.question}</h2>
                  <strong>{question.shortAnswer}</strong>
                  <p>{question.detailedAnswer}</p>
                  {question.redFlags && <aside role="alert"><b>When to seek medical help</b><p>{question.redFlags}</p></aside>}
                </article>
              ))}
            </section>
          </>
        ) : (
          <>
            <header className="questions-fallback-hero">
              <p className="eyebrow">Questions</p>
              <h1 data-testid="questions-index-heading">Perimenopause questions, answered carefully.</h1>
              <p>Browse clear, cautious answers organised around the patterns women commonly notice and ask about.</p>
            </header>
            <section className="questions-cluster-grid" aria-label="Question topics" data-testid="questions-cluster-grid">
              {CLUSTERS.map(([clusterSlug, title, intro]) => (
                <a href={`/questions/${clusterSlug}`} key={clusterSlug} data-testid={`questions-cluster-${clusterSlug}`}>
                  <span>Explore topic</span><h2>{title}</h2><p>{intro}</p>
                </a>
              ))}
            </section>
          </>
        )}
        {!questions.length && !failed && <p className="questions-load-state" data-testid="questions-loading">Loading questions…</p>}
        {failed && <p className="questions-load-state" role="alert" data-testid="questions-error">Questions are temporarily unavailable.</p>}
      </main>
      <Footer />
    </div>
  );
}